import { create } from 'zustand';

export type AuthStatus = 'checking' | 'anonymous' | 'authenticated' | 'forbidden';

export interface BaseAuthPayload {
  username: string | null;
  displayName: string | null;
  roles: string[];
}

export type AuthPayload<TExtra extends object = object> = BaseAuthPayload & TExtra;

export interface CreateAuthStoreOptions<
  TRole extends string,
  THighestRoleKey extends string,
  TExtra extends object = Record<string, never>
> {
  highestRoleKey: THighestRoleKey;
  emptyExtra?: TExtra;
  getHighestRole: (roles: string[]) => TRole | null;
}

export interface PlatformAuthState<TExtra extends object = object> {
  status: AuthStatus;
  username: string | null;
  displayName: string | null;
  roles: string[];
  setChecking: () => void;
  setAnonymous: () => void;
  setForbidden: () => void;
  setAuthenticated: (payload: AuthPayload<TExtra>) => void;
  reset: () => void;
}

export function createAuthStore<
  TRole extends string,
  THighestRoleKey extends string,
  TExtra extends object = Record<string, never>
>(
  options: CreateAuthStoreOptions<TRole, THighestRoleKey, TExtra>
) {
  type HighestRoleState = { [K in THighestRoleKey]: TRole | null };
  type State = PlatformAuthState<TExtra> & TExtra & HighestRoleState;

  const emptyBase = {
    username: null,
    displayName: null,
    roles: [] as string[]
  };
  const emptyExtra = options.emptyExtra ?? ({} as TExtra);
  const empty = {
    ...emptyBase,
    ...emptyExtra,
    [options.highestRoleKey]: null
  } as Pick<State, 'username' | 'displayName' | 'roles'> & TExtra & HighestRoleState;

  return create<State>((set) => ({
    status: 'checking',
    ...empty,
    setChecking: () => set({ status: 'checking', ...empty } as Partial<State>),
    setAnonymous: () => set({ status: 'anonymous', ...empty } as Partial<State>),
    setForbidden: () => set({ status: 'forbidden', ...empty } as Partial<State>),
    setAuthenticated: (payload) => {
      const highestRole = options.getHighestRole(payload.roles);
      if (!highestRole) {
        set({
          status: 'forbidden',
          ...payload,
          [options.highestRoleKey]: null
        } as Partial<State>);
        return;
      }
      set({
        status: 'authenticated',
        ...payload,
        username: payload.username ?? 'User',
        [options.highestRoleKey]: highestRole
      } as Partial<State>);
    },
    reset: () => set({ status: 'anonymous', ...empty } as Partial<State>)
  }));
}
